/**
 * OrgChartTab - World-Class Minimalist Design
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Loader2, Crown, Building2, Briefcase, 
  List, LayoutGrid, Shield
} from 'lucide-react';

const translations = {
  en: {
    orgChart: 'Organization Chart',
    subtitle: 'Visualize your team structure',
    users: 'users',
    partners: 'partners',
    hierarchy: 'Chart',
    list: 'List',
    partnersLevel: 'Partners',
    associatesLevel: 'Associates', 
    supportLevel: 'Support',
    departmentView: 'Departments',
    members: 'members',
    loading: 'Loading...'
  },
  no: {
    orgChart: 'Organisasjonskart',
    subtitle: 'Visualiser teamstrukturen',
    users: 'brukere',
    partners: 'partnere',
    hierarchy: 'Kart',
    list: 'Liste',
    partnersLevel: 'Partnere',
    associatesLevel: 'Advokater',
    supportLevel: 'Støtte',
    departmentView: 'Avdelinger',
    members: 'medlemmer',
    loading: 'Laster...'
  }
};

const HIERARCHY_LEVELS = [
  { 
    id: 'partners',
    titles: ['Managing Partner', 'Senior Partner', 'Partner', 'Of Counsel'],
    color: 'violet'
  },
  { 
    id: 'associates',
    titles: ['Senior Associate', 'Associate', 'Junior Associate', 'Trainee'],
    color: 'blue'
  },
  { 
    id: 'support',
    titles: ['Paralegal', 'Legal Secretary', 'Office Manager', 'Receptionist'],
    color: 'slate'
  }
];

const getColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
    slate: { bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-600' }
  };
  return colors[color] || colors.slate;
};

interface OrgChartTabProps {
  orgChart: any;
  onRefresh?: () => void;
  language?: string;
}

const OrgChartTab: React.FC<OrgChartTabProps> = ({ orgChart, onRefresh, language = 'no' }) => {
  const t = translations[language as keyof typeof translations] || translations.no;
  const [viewMode, setViewMode] = useState('chart');
  
  if (!orgChart) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const getAllUsers = () => {
    const usersMap = new Map();
    orgChart.leadership?.forEach((u: any) => usersMap.set(u.id, u));
    orgChart.departments?.forEach((dept: any) => {
      dept.teams?.forEach((team: any) => {
        team.members?.forEach((m: any) => usersMap.set(m.id, m));
      });
    });
    orgChart.users_without_team?.forEach((u: any) => usersMap.set(u.id, u));
    return Array.from(usersMap.values());
  };

  const groupByLevel = () => {
    const allUsers = getAllUsers();
    return HIERARCHY_LEVELS.map(level => ({
      ...level,
      label: t[`${level.id}Level` as keyof typeof t] || level.id,
      users: allUsers.filter(u => level.titles.includes(u.job_title))
    })).filter(level => level.users.length > 0);
  };

  const groupedUsers = groupByLevel();

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">{t.orgChart}</h2>
          <p className="text-slate-500 text-sm mt-1">{t.subtitle}</p>
        </div>
        
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          <button
            onClick={() => setViewMode('chart')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              viewMode === 'chart' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            {t.hierarchy}
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
            }`}
          >
            <List className="w-4 h-4" />
            {t.list}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mb-6">
        <div className="bg-slate-50 px-4 py-2 rounded-xl text-sm">
          <span className="text-slate-900 font-medium">{orgChart.stats?.total_users || 0}</span>
          <span className="text-slate-400 ml-1">{t.users}</span>
        </div>
        <div className="bg-violet-50 px-4 py-2 rounded-xl text-sm border border-violet-200">
          <span className="text-violet-700 font-medium">{orgChart.stats?.total_partners || 0}</span>
          <span className="text-violet-600 ml-1">{t.partners}</span>
        </div>
      </div>

      {viewMode === 'chart' ? (
        <ChartView orgChart={orgChart} groupedUsers={groupedUsers} t={t} />
      ) : (
        <ListView groupedUsers={groupedUsers} t={t} />
      )}
    </div>
  );
};

const ChartView: React.FC<{ orgChart: any; groupedUsers: any[]; t: any }> = ({ orgChart, groupedUsers, t }) => {
  return (
    <div className="space-y-8">
      {/* Company Header */}
      <div className="flex justify-center">
        <div className="bg-slate-900 rounded-xl px-6 py-3 text-white shadow-lg flex items-center gap-3">
          <Crown className="w-5 h-5 text-amber-400" />
          <span className="font-semibold">{orgChart.tenant?.name}</span>
        </div>
      </div>

      {/* Hierarchy Levels */}
      {groupedUsers.map((level, levelIndex) => {
        const colors = getColorClasses(level.color);
        
        return (
          <motion.div 
            key={level.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: levelIndex * 0.1 }}
          >
            <div className="flex justify-center mb-3">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                {level.label}
              </span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3">
              {level.users.map((user: any, i: number) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className={`${colors.bg} ${colors.border} border rounded-xl p-3 w-28 text-center hover:shadow-md transition-shadow`}
                >
                  <div className="w-10 h-10 bg-white rounded-lg mx-auto mb-2 flex items-center justify-center shadow-sm overflow-hidden">
                    {user.profile_picture_url ? (
                      <img src={user.profile_picture_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-semibold text-slate-600">
                        {user.name?.charAt(0)}
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-xs text-slate-900 truncate">{user.name}</p>
                  <p className={`text-[10px] ${colors.text} truncate`}>{user.job_title}</p>
                  {user.role === 'ADMIN' && (
                    <div className="flex items-center justify-center gap-0.5 mt-1">
                      <Shield className="w-2.5 h-2.5 text-slate-400" />
                      <span className="text-[9px] text-slate-400">Admin</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            
            {levelIndex < groupedUsers.length - 1 && (
              <div className="flex justify-center my-4">
                <div className="w-0.5 h-6 bg-slate-200"></div>
              </div>
            )}
          </motion.div>
        );
      })}

      {/* Departments */}
      {orgChart.departments?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-slate-200">
          <h4 className="text-center text-xs font-medium text-slate-400 uppercase tracking-wider mb-4">
            {t.departmentView}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {orgChart.departments.map((dept: any) => (
              <div key={dept.id} className="bg-slate-50/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-sm text-slate-900">{dept.name}</span>
                </div>
                
                {dept.teams?.map((team: any) => (
                  <div key={team.id} className="bg-white rounded-lg p-2.5 mb-2 last:mb-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-slate-700">{team.name}</span>
                      <span className="text-[10px] text-slate-400">{team.members?.length} {t.members}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {team.members?.slice(0, 5).map((m: any) => (
                        <div 
                          key={m.id}
                          className="w-6 h-6 bg-slate-100 rounded text-[10px] font-medium text-slate-500 flex items-center justify-center"
                          title={`${m.name} - ${m.job_title}`}
                        >
                          {m.name?.charAt(0)}
                        </div>
                      ))}
                      {team.members?.length > 5 && (
                        <div className="w-6 h-6 bg-slate-200 rounded text-[10px] font-medium text-slate-500 flex items-center justify-center">
                          +{team.members.length - 5}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ListView: React.FC<{ groupedUsers: any[]; t: any }> = ({ groupedUsers, t }) => {
  return (
    <div className="space-y-4">
      {groupedUsers.map((level) => {
        const colors = getColorClasses(level.color);
        
        return (
          <div key={level.id} className="bg-slate-50/50 rounded-xl overflow-hidden">
            <div className={`${colors.bg} px-4 py-2.5 border-b ${colors.border}`}>
              <h4 className="font-medium text-sm text-slate-900">
                {level.label} ({level.users.length})
              </h4>
            </div>
            <div className="divide-y divide-slate-100">
              {level.users.map((user: any) => (
                <div key={user.id} className="px-4 py-3 flex items-center gap-3 hover:bg-white transition-colors">
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center text-sm font-medium text-slate-600 overflow-hidden shadow-sm">
                    {user.profile_picture_url ? (
                      <img src={user.profile_picture_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      user.name?.charAt(0)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-lg ${colors.bg} ${colors.text} border ${colors.border}`}>
                    {user.job_title}
                  </span>
                  {user.role === 'ADMIN' && (
                    <Shield className="w-4 h-4 text-slate-400" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrgChartTab;
